// Card #1637 — the scoreboard's shared venue-local time formatter (design
// docs/score/scoreboard-design.md §4.2/§8.4). This is meant to be the LAST
// venue-local time formatter anybody writes for the scoreboard surface:
// every later scoreboard card renders times through this, not a bespoke
// Intl call of its own.
//
// #1788 — this used to export a hardcoded
// `VENUE_TIMEZONE = "America/Los_Angeles"`, on the stated grounds that no
// score-show.timeZone field existed. That stopped being true: WEC (Ocala,
// Florida) is Eastern, so every time the board rendered for it was 3 hours
// early. The constant is now a DEFAULT; the real zone rides down from the
// API on the scoreboard poll response's `venueTimezone` key and is passed
// in explicitly by callers.
//
// This is a deliberate DUPLICATE of apps/score-api/src/lib/venue-time.js's
// resolver, matching how the two constants were already duplicated: the FE
// bundle must not import score-api, and both sides have to agree on what a
// stored offset means. The two are kept in sync by identical unit tests
// pinning the same WEC instant to the same clock face on both sides.
export const DEFAULT_VENUE_TIMEZONE = "America/Los_Angeles";

// 🔴 #1811 — the zone FVD STAMPS ARE MINTED IN. This is NOT the venue's zone,
// it only happens to share a value with the Pacific default above, and the two
// must never be collapsed back into one constant.
//
// `scheduledTime` is not a genuine UTC instant. FVD's Access DB holds a naive
// venue wall-clock time; ADODB on `fvdts` parses that naive value as
// BOX-local, and `fvdts` is set to America/Los_Angeles; foxbox then reads it
// back with UTC getters. So WEC's 8:08 AM EASTERN ride is stored as
// 2026-08-21T15:08:00.000Z — which is 11:08 AM Eastern, three hours off the
// truth. (Measured on dev, 2026-08-21, show fvd:show:showKey-oes:e83e7ef8….)
//
// Rendering that stamp back through Pacific is the EXACT INVERSE of the
// mangling, which is the only reason class times have ever displayed
// correctly. The Pacific here is load-bearing, not a leftover from before
// `Show.venueTimezone` existed.
//
// So: anything sourced from FVD (`scheduledTime`, and everything derived from
// it — `classStart`, `estimatedFinish`, `rideTime`) renders through THIS
// constant, whatever venue the show is at. Genuine instants (`serverTime`,
// `lastUpdated`, the live clock) render through the show's `venueTimezone`.
// Point a stamp at the venue zone and every ride time jumps by the offset
// between `fvdts` and the venue — three hours late at WEC.
//
// This disappears only when ingest is fixed to stamp genuine UTC and the
// existing rows are migrated (card #1814). Until then, deleting this is a
// three-hour production bug that looks like a cleanup — which is exactly what
// card #1805 proposed before it was measured.
export const FVD_STAMP_TIMEZONE = "America/Los_Angeles";

// Shows are configured with a GMT-n OFFSET, not an IANA zone name (Jaybill,
// 2026-08-20). `Intl` rejects a bare "GMT-4", but the POSIX-style
// `Etc/GMT±N` zones mean exactly "this fixed offset, no DST", so mapping
// onto one lets every existing Intl call keep working with only the zone
// STRING differing.
//
// 🔴 The sign is INVERTED and that is correct: POSIX `Etc/GMT+4` is four
// hours BEHIND UTC (what a human calls "GMT-4"). Backwards is an 8-hour
// error, not a no-op, which is why the tests pin a real instant to a real
// clock face rather than asserting on the mapping.
const OFFSET_PATTERN = /^(?:GMT|UTC)?\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?$/i;

const MIN_OFFSET_HOURS = -14;
const MAX_OFFSET_HOURS = 12;

const zoneValidityCache = new Map();

function isZoneUsable(timeZone) {
  if (zoneValidityCache.has(timeZone)) return zoneValidityCache.get(timeZone);
  let usable = true;
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone });
  } catch {
    usable = false;
  }
  zoneValidityCache.set(timeZone, usable);
  return usable;
}

/**
 * Turn a show's configured venue timezone into a string `Intl` accepts.
 * Accepts "GMT-4"/"UTC-4"/"-4"/"-04:00" or an IANA name. Anything absent,
 * malformed, out of range, or not a whole hour resolves to the Pacific
 * default — this NEVER throws (#1798: the board never shows an error).
 */
export function resolveVenueTimeZone(configured) {
  if (typeof configured !== "string") return DEFAULT_VENUE_TIMEZONE;
  const trimmed = configured.trim();
  if (!trimmed) return DEFAULT_VENUE_TIMEZONE;

  if (/^(?:GMT|UTC)$/i.test(trimmed)) return "Etc/GMT";

  const match = OFFSET_PATTERN.exec(trimmed);
  if (match) {
    const [, sign, hoursRaw, minutesRaw] = match;
    if (minutesRaw && Number(minutesRaw) !== 0) return DEFAULT_VENUE_TIMEZONE;
    const hours = Number(hoursRaw) * (sign === "-" ? -1 : 1);
    if (hours < MIN_OFFSET_HOURS || hours > MAX_OFFSET_HOURS) return DEFAULT_VENUE_TIMEZONE;
    if (hours === 0) return "Etc/GMT";
    return `Etc/GMT${hours < 0 ? "+" : "-"}${Math.abs(hours)}`;
  }

  return isZoneUsable(trimmed) ? trimmed : DEFAULT_VENUE_TIMEZONE;
}

// Accepts a Date, an ISO string, or an epoch number; returns null for a
// value that fails to parse — a scoreboard must never throw while rendering
// a malformed timestamp, it should just render a blank time.
function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value == null) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "8:10 AM" (or "8:10:05 AM" with `seconds: true`), venue-local — never UTC,
// never the browser's own zone. `timeZone` accepts a configured offset or an
// IANA name; omitting it falls back to Pacific. Returns "" for an
// unparseable/absent value so a caller can render it directly without an
// extra guard.
export function formatVenueTime(value, { timeZone = DEFAULT_VENUE_TIMEZONE, seconds = false } = {}) {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    timeZone: resolveVenueTimeZone(timeZone),
  });
}

// The venue-local calendar day ("2026-08-09") a timestamp falls on. en-CA's
// default date format happens to BE "YYYY-MM-DD", a dependency-free way to
// get a sortable/comparable key out of Intl instead of hand-rolling one.
// Deliberately NOT a string-slice of the ISO value: a timestamp stamped late
// evening venue-local can already be the next day in UTC, and this must
// still resolve to the venue day a human at the show would name.
export function venueDayKey(value, timeZone = DEFAULT_VENUE_TIMEZONE) {
  const d = toDate(value);
  if (!d) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: resolveVenueTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

// #1811 — render an FVD-sourced timestamp (`scheduledTime`, `rideTime`,
// `classStart`, `estimatedFinish`). Deliberately takes NO `timeZone` option:
// there is exactly one right answer (see FVD_STAMP_TIMEZONE above), and a
// caller that can pass a zone is a caller that will eventually be handed the
// venue's. The separate name is the guardrail — `formatVenueTime(stamp, {
// timeZone })` and `formatFvdStampTime(stamp)` look different at the call
// site, which is the whole point.
export function formatFvdStampTime(value, { seconds = false } = {}) {
  return formatVenueTime(value, { timeZone: FVD_STAMP_TIMEZONE, seconds });
}

// The calendar day an FVD stamp falls on, in the same stamp zone. Same
// reasoning as formatFvdStampTime: a stamp's day must be recovered through
// the zone it was minted in, not the venue's.
export function fvdStampDayKey(value) {
  return venueDayKey(value, FVD_STAMP_TIMEZONE);
}

export default formatVenueTime;
